import {type ReactNode, useCallback, useEffect, useMemo, useState} from "react";
import {useEncounter} from "@/encounter/EncounterHooks.ts";
import {useRealtimeChannel} from "@/api/realtime/RealtimeChannelHooks.ts";
import {getDoorStates, saveDoorState as persistDoorState} from "@/api/DoorStateApi.ts";
import {DoorStateContext} from "./DoorStateHooks.ts";
import {effectiveDoorState, type DoorState, type EncounterDoorState} from "./DoorState.ts";
import type {Door} from "@/dungeons/model/DungeonGeometry.ts";

export default function DoorStateProvider({children}: { children: ReactNode }) {
    const encounter = useEncounter();
    const {subscribe} = useRealtimeChannel<EncounterDoorState>(`/encounters/${encounter.id}/doorStates`);
    const [overrides, setOverrides] = useState<ReadonlyMap<string, DoorState>>(new Map());
    const [initialLoad, setInitialLoad] = useState<{encounterId?: string; error: boolean}>({error: false});

    useEffect(() => {
        let active = true;
        let initialSnapshotLoaded = false;
        const buffered: EncounterDoorState[] = [];

        const apply = (doorState: EncounterDoorState) => setOverrides(current => {
            const next = new Map(current);
            next.set(doorState.doorId, doorState.state);
            return next;
        });
        const unsubscribe = subscribe(doorState => {
            if (!active) return;
            if (initialSnapshotLoaded) apply(doorState);
            else buffered.push(doorState);
        });

        void getDoorStates(encounter).then(states => {
            if (!active) return;
            const next = new Map(states.map(state => [state.doorId, state.state]));
            buffered.forEach(state => next.set(state.doorId, state.state));
            setOverrides(next);
            initialSnapshotLoaded = true;
            setInitialLoad({encounterId: encounter.id, error: false});
        }).catch(() => {
            if (active) setInitialLoad({encounterId: encounter.id, error: true});
        });

        return () => {
            active = false;
            unsubscribe();
        };
    }, [encounter, subscribe]);

    const stateFor = useCallback(
        (door: Door) => effectiveDoorState(door, overrides),
        [overrides],
    );
    const setDoorState = useCallback(async (door: Door, state: DoorState) => {
        const saved = await persistDoorState(encounter, door.id, state);
        setOverrides(current => new Map(current).set(saved.doorId, saved.state));
    }, [encounter]);
    const value = useMemo(() => ({
        initialLoading: initialLoad.encounterId !== encounter.id,
        stateFor,
        setDoorState,
    }), [encounter.id, initialLoad, setDoorState, stateFor]);

    if (initialLoad.error) throw new Error("Türzustände konnten nicht geladen werden.");
    return <DoorStateContext.Provider value={value}>{children}</DoorStateContext.Provider>;
}
